import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { getCalendarDays, formatDate } from '@/utils/dateUtils';

const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

export default function Calendar() {
  const navigate = useNavigate();
  const { memories } = useStore();
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  // 获取日历天数
  const calendarDays = useMemo(() => {
    return getCalendarDays(year, month);
  }, [year, month]);

  // 获取每天的记录数
  const memoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    memories.forEach(memory => {
      const date = memory.createdAt.split('T')[0];
      counts[date] = (counts[date] || 0) + 1;
    });
    return counts;
  }, [memories]);

  // 获取选中日期或今天的记录
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const todayMemories = useMemo(() => {
    const targetDate = selectedDate || new Date().toISOString().split('T')[0];
    return memories.filter(m => m.createdAt.startsWith(targetDate));
  }, [memories, selectedDate]);

  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="px-4 py-4">
      {/* 月份选择器 */}
      <div className="bg-white rounded-2xl p-4 card-shadow mb-4">
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={goToPrevMonth}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <span className="text-lg font-bold text-gray-800">
              {year}年{month}月
            </span>
          </div>
          <button 
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* 星期标题 */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="text-center text-sm text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* 日历网格 */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map(({ date, day, isCurrentMonth }) => {
            const count = memoryCounts[date] || 0;
            const isToday = date === today;
            const isSelected = date === selectedDate;

            return (
              <button
                key={date}
                onClick={() => setSelectedDate(date === selectedDate ? null : date)}
                className={`
                  relative aspect-square rounded-xl flex flex-col items-center justify-center
                  transition-all duration-200
                  ${isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}
                  ${isSelected ? 'bg-primary-500 text-white' : 'hover:bg-gray-50'}
                  ${isToday && !isSelected ? 'ring-2 ring-primary-400 bg-primary-50' : ''}
                `}
              >
                <span className={`text-sm font-medium ${isSelected ? 'text-white' : ''}`}>
                  {day}
                </span>
                {count > 0 && (
                  <div className={`
                    absolute bottom-1 w-1.5 h-1.5 rounded-full
                    ${isSelected ? 'bg-white' : 'bg-primary-400'}
                  `} />
                )}
              </button>
            );
          })}
        </div>

        {/* 回到今天 */}
        <button
          onClick={goToToday}
          className="w-full mt-4 py-2 text-sm text-primary-500 font-medium hover:bg-primary-50 rounded-lg transition-colors"
        >
          回到今天
        </button>
      </div>

      {/* 选中日期的记录 */}
      <div className="bg-white rounded-2xl p-4 card-shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">
            {selectedDate ? formatDate(selectedDate) : '今天'}
          </h3>
          <span className="text-sm text-gray-500">
            {todayMemories.length} 条记录
          </span>
        </div>

        {todayMemories.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">这一天还没有记录</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todayMemories.map(memory => (
              <div
                key={memory.id}
                onClick={() => navigate(`/memory/${memory.id}`)}
                className="p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <p className="text-sm text-gray-700 line-clamp-2">{memory.content}</p>
                {memory.media.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {memory.media.slice(0, 3).map(media => (
                      <div 
                        key={media.id}
                        className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200"
                      >
                        {media.type === 'image' ? (
                          <img 
                            src={media.url} 
                            alt="" 
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <video 
                            src={media.url}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="h-20" />
    </div>
  );
}
